import {useState, useContext, useEffect} from 'react';
import {AxiosContext} from '../components/utils/AxiosProvider';

let debouncedQuery;
const useGetCourseCodes = (course, school, useCache = true, cache = true) => {
    const [isLoading, setIsLoading] = useState(false);
    const [codes, setCodes] = useState(null);
    const [error, setError] = useState(null);
    const {client: axiosClient} = useContext(AxiosContext);

    useEffect(() => {
        if (axiosClient) {
            if (debouncedQuery) {
                clearTimeout(debouncedQuery);
                debouncedQuery = null;
            }
            const fetchCodes = async () => {
                try {
                    // Replace the promise with the axios call
                    const courses = await axiosClient.get(
                        `/api/1/courses/filter?${
                            course ? `course_name=${course}` : ''
                        }&${
                            school?.school_name
                                ? `school_name=${school.school_name}`
                                : ''
                        }`,
                        {useCache: useCache, cache: cache}
                    );
                    const numbers = await axiosClient.get(
                        `/api/1/courses/filter?${
                            course ? `course_number=${course}` : ''
                        }&${
                            school?.school_name
                                ? `school_name=${school.school_name}`
                                : ''
                        }`,
                        {useCache: useCache, cache: cache}
                    );

                    setError(null);
                    const number = new Set(numbers.data.courses);
                    const title = new Set(courses.data.courses);

                    for (const numCourse of number) {
                        title.add(numCourse);
                    }

                    setCodes([...title]);
                } catch (e) {
                    setError(e);
                    setCodes(null);
                }

                setIsLoading(false);
            };
            setCodes(null);

            if (course && course.length > 2) {
                // Invoke the endpoint to get data
                setIsLoading(true);
                debouncedQuery = setTimeout(() => fetchCodes(), 500);
            } else if (school) {
                // User just selected a school so let's load all courses now
                setIsLoading(true);
                debouncedQuery = setTimeout(() => fetchCodes(), 0);
            }
        }
    }, [course, school, axiosClient, useCache, cache]);

    return {
        codes,
        isLoading,
        error,
    };
};

const useGetCourses = (
    courseName = null,
    courseNumber = null,
    schoolName = null,
    state = null,
    useCache = true,
    cache = true
) => {
    const [isLoading, setIsLoading] = useState(false);
    const [courses, setCourses] = useState(null);
    const [error, setError] = useState(null);
    const {client: axiosClient} = useContext(AxiosContext);

    useEffect(() => {
        if (axiosClient) {
            if (debouncedQuery) {
                clearTimeout(debouncedQuery);
                debouncedQuery = null;
            }
            const fetchCodes = async () => {
                try {
                    // Replace the promise with the axios call
                    const courses = await axiosClient.get(
                        '/api/1/courses/filter',
                        {
                            useCache: useCache,
                            cache: cache,
                            params: {
                                course_name: courseName || null,
                                course_number: courseNumber || null,
                                school_name: schoolName || null,
                                state: state || null,
                            },
                        }
                    );
                    setError(null);
                    const title = new Set(courses.data.courses);

                    setCourses([...title]);
                } catch (e) {
                    setError(e);
                    setCourses(null);
                }

                setIsLoading(false);
            };
            setCourses(null);

            if (
                (courseName && courseName.length > 2) ||
                courseNumber ||
                schoolName
            ) {
                // Invoke the endpoint to get data
                setIsLoading(true);
                debouncedQuery = setTimeout(() => fetchCodes(), 500);
            } else if (state) {
                // User just selected a state so let's load all courses now
                setIsLoading(true);
                debouncedQuery = setTimeout(() => fetchCodes(), 0);
            } else {
                setIsLoading(false);
            }
        }
    }, [
        courseName,
        courseNumber,
        schoolName,
        state,
        axiosClient,
        useCache,
        cache,
    ]);

    return {
        courses,
        isLoading,
        error,
    };
};

export default useGetCourseCodes;
export {useGetCourses};
